import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ProjectContext } from '../../../state/project-state/project-state';
import { nameof } from '../../../utils/nameof';
import { CheckBox } from '../../check-box/check-box';
import { Icon } from '../../icon/icon';
import { Table } from '../../table/table';

type Props = {
  displayCheckbox?: boolean;
  disableCheckbox?: boolean;
  checked?: boolean;
  displayCanBeAddedToSkyViewColumn?: boolean;
  displaySkyMapConnectColumn: boolean;
  displaySharedColumn: boolean;
  displayTagColumn: boolean;
  checkVolatileName: boolean;
  onSelectionChanged: (selected: boolean) => void;
};

const HeaderRow = React.memo(({ displayCheckbox = true, ...props }: Props) => {
  const { t } = useTranslation();
  const checkBoxRef = React.useRef<HTMLInputElement>(null);
  const { sharedFolderId } = React.useContext(ProjectContext);
  const { onSelectionChanged: propsOnSelectionChanged } = props;

  const onCheckBoxCellClick = () => {
    checkBoxRef.current?.click();
  };

  const onCheckBoxChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.target.indeterminate) {
        propsOnSelectionChanged(e.target.checked);
      }
    },
    [propsOnSelectionChanged],
  );

  return (
    <Component>
      {displayCheckbox && !sharedFolderId && (
        <Table.HeaderCell
          padding={{ left: 0.7, right: 0.7 }}
          shrink={true}
          onClick={onCheckBoxCellClick}
          onHoverBackgroundColor={'#EEE'}
        >
          <CheckBox
            checked={props.checked}
            disabled={props.disableCheckbox ?? false}
            ref={checkBoxRef}
            onChange={onCheckBoxChange}
          />
        </Table.HeaderCell>
      )}

      <Table.HeaderCell padding={{ left: 0.4 }}>
        {t('fileManager.folderListView.columns.name', {
          ns: 'components',
        })}
      </Table.HeaderCell>

      {props.displayCanBeAddedToSkyViewColumn && (
        <Table.HeaderCell shrink={true}>
          <Icon
            icon={['fad', 'drone']}
            title={t('fileManager.folderListView.columns.availableInSkyView', {
              ns: 'components',
            })}
          />
        </Table.HeaderCell>
      )}

      {props.checkVolatileName && (
        <Table.HeaderCell shrink={true} wrapping={'none'}>
          <Icon
            icon={['fad', 'exclamation-triangle']}
            swapOpacity={true}
            title={t('fileManager.folderListView.columns.checkVolatileName', {
              ns: 'components',
            })}
          />
        </Table.HeaderCell>
      )}

      {props.displaySkyMapConnectColumn && (
        <Table.HeaderCell shrink={true} wrapping={'none'}>
          <Icon icon={['fad', 'puzzle-piece']} title="SkyMap Connect" />
        </Table.HeaderCell>
      )}

      {props.displaySharedColumn && (
        <Table.HeaderCell shrink={true} wrapping={'none'}>
          <Icon
            icon={['fad', 'share-nodes']}
            title={t('fileManager.folderListView.columns.sharedFolder', {
              ns: 'components',
            })}
          />
        </Table.HeaderCell>
      )}

      {props.displayTagColumn && (
        <Table.HeaderCell shrink={true} wrapping={'none'}>
          <Icon
            icon={['fad', 'tag']}
            swapOpacity={true}
            title={t('fileManager.folderListView.columns.tags', {
              ns: 'components',
            })}
          />
        </Table.HeaderCell>
      )}

      <Table.HeaderCell shrink={true} wrapping={'none'}>
        {t('fileManager.folderListView.columns.type', {
          ns: 'components',
        })}
      </Table.HeaderCell>

      <Table.HeaderCell shrink={true} wrapping={'none'}>
        {t('fileManager.folderListView.columns.size', {
          ns: 'components',
        })}
      </Table.HeaderCell>

      <Table.HeaderCell shrink={true} wrapping={'none'}>
        {t('fileManager.folderListView.columns.contains', {
          ns: 'components',
        })}
      </Table.HeaderCell>

      <Table.HeaderCell shrink={true} wrapping={'none'}>
        {t('fileManager.folderListView.columns.lastChanged', {
          ns: 'components',
        })}
      </Table.HeaderCell>
    </Component>
  );
});

HeaderRow.displayName = nameof({ HeaderRow });

const Component = styled(Table.Row)``;

export { HeaderRow };
